import React, { useEffect, useState } from 'react'
import LayoutHeader from '../../../../layout/LayoutHeader'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { setErrorAlert, setSuccessAlert, setLoader } from '../../../../store/alert/alertSlice'
import { postDataAPI, getDataAPI } from '../../../../utils/fetchData'
import DizeeInput from '../../../../components/DizeeComponent/DizeeInput'

export default function EmailSearch() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [isSearching, setIsSearching] = useState(false)
    const [selectedEmail, setSelectedEmail] = useState(null)
    const [aiAnalysis, setAiAnalysis] = useState(null)
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [searchFilters, setSearchFilters] = useState({
        from: '',
        subject: '',
        dateRange: '30', // days
        hasAttachment: false,
        isUnread: false
    })

    const handleSearch = async () => {
        if (!searchQuery.trim() && !searchFilters.from && !searchFilters.subject) {
            dispatch(setErrorAlert('Please enter a search query or filter'))
            return
        }

        try {
            setIsSearching(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/search', {
                query: searchQuery.trim(),
                filters: searchFilters
            }, token)
            
            if (response.data.status === 200) {
                setSearchResults(response.data.emails || [])
                if (response.data.emails.length === 0) {
                    dispatch(setSuccessAlert('No emails found matching your search'))
                }
            } else {
                dispatch(setErrorAlert(response.data.message || 'Failed to search emails'))
            }
        } catch (error) {
            console.error('Error searching emails:', error)
            dispatch(setErrorAlert('Error searching emails'))
        } finally {
            setIsSearching(false)
            dispatch(setLoader(false))
        }
    }

    const handleAnalyzeEmail = async (email) => {
        try {
            setIsAnalyzing(true)
            setSelectedEmail(email)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/analyze-email', {
                emailId: email.id,
                includeContext: true
            }, token)
            
            if (response.data.status === 200) {
                setAiAnalysis(response.data.analysis)
            } else {
                dispatch(setErrorAlert('Failed to analyze email'))
            }
        } catch (error) {
            console.error('Error analyzing email:', error)
            dispatch(setErrorAlert('Error analyzing email'))
        } finally {
            setIsAnalyzing(false)
            dispatch(setLoader(false))
        }
    }

    const handleBulkAnalysis = async () => {
        if (searchResults.length === 0) {
            dispatch(setErrorAlert('No emails to analyze'))
            return
        }

        try {
            setIsAnalyzing(true)
            dispatch(setLoader(true))
            
            const token = JSON.parse(localStorage.getItem('dizeeToken'))
            const response = await postDataAPI('user/gmail/bulk-analyze', {
                emailIds: searchResults.map(email => email.id),
                analysisType: 'music_industry_insights'
            }, token)
            
            if (response.data.status === 200) {
                setAiAnalysis(response.data.analysis)
                dispatch(setSuccessAlert('Bulk analysis completed'))
            } else {
                dispatch(setErrorAlert('Failed to analyze emails'))
            }
        } catch (error) {
            console.error('Error analyzing emails:', error)
            dispatch(setErrorAlert('Error analyzing emails'))
        } finally {
            setIsAnalyzing(false)
            dispatch(setLoader(false))
        }
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getEmailPreview = (content) => {
        if (!content) return 'No preview available'
        return content.length > 150 ? content.substring(0, 150) + '...' : content
    }

    const getAnalysisTypeColor = (type) => {
        switch (type) {
            case 'booking_opportunity': return 'bg-green-500'
            case 'collaboration_request': return 'bg-blue-500'
            case 'industry_update': return 'bg-yellow-500'
            case 'promotional_opportunity': return 'bg-purple-500'
            case 'business_inquiry': return 'bg-orange-500'
            default: return 'bg-gray-500'
        }
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
                <div className="px-4 my-[50px] flex w-full justify-between">
                    <p className="text-white">Email Search & AI Analysis</p>
                    <div className='flex gap-[20px]'>
                        <p onClick={() => navigate('/more/setting/gmail')} className='text-white cursor-pointer'>Go back</p>
                    </div>
                </div>

                {/* Search Form */}
                <div className="w-full px-4 mb-6">
                    <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#ffffff1a]">
                        <h3 className="text-white text-sm font-medium mb-4">Search Your Emails</h3>
                        
                        <div className="space-y-3">
                            <DizeeInput
                                placeholder="Search emails (e.g., 'booking', 'show', 'collaboration')"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            
                            <div className="grid grid-cols-2 gap-3">
                                <DizeeInput
                                    placeholder="From email"
                                    value={searchFilters.from}
                                    onChange={(e) => setSearchFilters({...searchFilters, from: e.target.value})}
                                />
                                
                                <DizeeInput
                                    placeholder="Subject contains"
                                    value={searchFilters.subject}
                                    onChange={(e) => setSearchFilters({...searchFilters, subject: e.target.value})}
                                />
                            </div>
                            
                            <div className="flex items-center gap-4">
                                <select
                                    value={searchFilters.dateRange}
                                    onChange={(e) => setSearchFilters({...searchFilters, dateRange: e.target.value})}
                                    className="bg-[#2a2a2a] text-white text-xs px-3 py-2 rounded border border-[#ffffff1a] flex-1"
                                >
                                    <option value="7">Last 7 days</option>
                                    <option value="30">Last 30 days</option>
                                    <option value="90">Last 90 days</option>
                                    <option value="365">Last year</option>
                                </select>
                                
                                <label className="flex items-center gap-2 text-xs text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={searchFilters.hasAttachment}
                                        onChange={(e) => setSearchFilters({...searchFilters, hasAttachment: e.target.checked})}
                                        className="rounded"
                                    />
                                    Has attachment
                                </label>
                            </div>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={handleSearch}
                                    disabled={isSearching}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors"
                                >
                                    {isSearching ? 'Searching...' : 'Search Emails'}
                                </button>
                                
                                {searchResults.length > 0 && (
                                    <button
                                        onClick={handleBulkAnalysis}
                                        disabled={isAnalyzing}
                                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors"
                                    >
                                        AI Analysis
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                    <div className="w-full px-4 mb-6">
                        <div className="bg-[#1a1a1a] rounded-lg border border-[#ffffff1a]">
                            <div className="p-4 border-b border-[#ffffff1a]">
                                <h3 className="text-white text-sm font-medium">
                                    Search Results ({searchResults.length})
                                </h3>
                            </div>
                            
                            <div className="divide-y divide-[#ffffff1a] max-h-96 overflow-y-auto">
                                {searchResults.map((email) => (
                                    <div key={email.id} className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white text-sm font-medium truncate">
                                                    {email.subject || 'No Subject'}
                                                </p>
                                                <p className="text-gray-300 text-xs">
                                                    From: {email.from}
                                                </p>
                                                <p className="text-gray-400 text-xs">
                                                    {formatDate(email.date)}
                                                </p>
                                            </div>
                                            
                                            <button
                                                onClick={() => handleAnalyzeEmail(email)}
                                                disabled={isAnalyzing}
                                                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors ml-2"
                                            >
                                                Analyze
                                            </button>
                                        </div>
                                        
                                        <p className="text-gray-300 text-xs leading-relaxed">
                                            {getEmailPreview(email.snippet)}
                                        </p>
                                        
                                        {email.hasAttachment && (
                                            <div className="mt-2">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-600 text-white">
                                                    📎 Attachment
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* AI Analysis Results */}
                {aiAnalysis && (
                    <div className="w-full px-4 mb-6">
                        <div className="bg-[#1a1a1a] rounded-lg border border-[#ffffff1a]">
                            <div className="p-4 border-b border-[#ffffff1a]">
                                <h3 className="text-white text-sm font-medium">AI Analysis Results</h3>
                                {selectedEmail && (
                                    <p className="text-gray-400 text-xs mt-1">
                                        Analyzing: {selectedEmail.subject}
                                    </p>
                                )}
                            </div>
                            
                            <div className="p-4 space-y-4">
                                {/* Analysis Type */}
                                {aiAnalysis.type && (
                                    <div className="flex items-center gap-2">
                                        <div className={`w-3 h-3 rounded-full ${getAnalysisTypeColor(aiAnalysis.type)}`}></div>
                                        <span className="text-white text-sm font-medium">
                                            {aiAnalysis.type.replace('_', ' ').toUpperCase()}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Key Insights */}
                                {aiAnalysis.insights && (
                                    <div>
                                        <h4 className="text-white text-sm font-medium mb-2">Key Insights</h4>
                                        <div className="space-y-2">
                                            {aiAnalysis.insights.map((insight, index) => (
                                                <p key={index} className="text-gray-300 text-xs leading-relaxed">
                                                    • {insight}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Action Items */}
                                {aiAnalysis.actionItems && aiAnalysis.actionItems.length > 0 && (
                                    <div>
                                        <h4 className="text-white text-sm font-medium mb-2">Suggested Actions</h4>
                                        <div className="space-y-2">
                                            {aiAnalysis.actionItems.map((action, index) => (
                                                <div key={index} className="bg-[#2a2a2a] rounded p-2">
                                                    <p className="text-green-400 text-xs font-medium">
                                                        {action.type}
                                                    </p>
                                                    <p className="text-gray-300 text-xs">
                                                        {action.description}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Summary */}
                                {aiAnalysis.summary && (
                                    <div>
                                        <h4 className="text-white text-sm font-medium mb-2">Summary</h4>
                                        <p className="text-gray-300 text-xs leading-relaxed">
                                            {aiAnalysis.summary}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Help Section */}
                <div className="w-full px-4 mt-6">
                    <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#ffffff1a]">
                        <h3 className="text-white text-sm font-medium mb-2">AI Analysis Features</h3>
                        <div className="text-gray-300 text-xs leading-relaxed space-y-2">
                            <p>• <strong>Booking Opportunities:</strong> Identifies potential show and venue opportunities</p>
                            <p>• <strong>Collaboration Requests:</strong> Detects partnership and collaboration offers</p>
                            <p>• <strong>Industry Updates:</strong> Highlights important industry news and changes</p>
                            <p>• <strong>Business Inquiries:</strong> Recognizes business and promotional opportunities</p>
                            <p className="mt-3 text-gray-400">
                                Use specific keywords like "booking", "show", "collaboration", "record label" for better results.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    )
} 